'use client';

/**
 * Native Push Notifications Bridge
 *
 * Uses @capacitor/push-notifications for APNs (iOS) and FCM (Android)
 * on native Capacitor shells. Falls back gracefully on web/PWA.
 *
 * The device token is stored to Supabase so the server can target
 * push notifications to specific providers.
 */

import { Capacitor } from '@capacitor/core';
// Static import, deliberately NOT a runtime import(): async chunk loads of
// Capacitor plugin modules hang inside the WKWebView shell (field-confirmed
// on iOS, 2026-07-03) — this lazy load is why device tokens never registered
// on any native install. The module is a tiny bridge proxy — safe on web/SSR.
import { PushNotifications } from '@capacitor/push-notifications';
import type { PermissionStatus } from '@capacitor/push-notifications';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PushRegistrationResult {
  success: boolean;
  token?: string;
  error?: string;
}

type PushNotificationHandler = (notification: {
  title?: string;
  body?: string;
  data?: Record<string, unknown>;
}) => void;

// ─── State ───────────────────────────────────────────────────────────────────

let _initialized = false;
let _deviceToken: string | null = null;
let _notificationHandler: PushNotificationHandler | null = null;
// The logged-in identity to attach the token to. The APNs/FCM token arrives
// asynchronously from initPushNotifications() at app startup, while
// saveDeviceToken() is called later (login / session restore). These two race,
// so we remember the identity here and let whichever fires last persist the
// token — guaranteeing it always reaches the server.
let _pendingIdentity: { providerId: string; authUserId: string } | null = null;

// ─── Plugin Access ───────────────────────────────────────────────────────────

function getPlugin() {
  return Capacitor.isNativePlatform() ? PushNotifications : null;
}

// Time-box every native round-trip: field evidence (2026-07-03) shows plugin
// calls in the shell can simply never answer, and an un-time-boxed await here
// silently kills everything downstream (this is why no device ever prompted
// for push permission). Distinct labels let the launch beacon name the exact
// call that hangs.
const NATIVE_CALL_TIMEOUT_MS = 5000;
const TIMED_OUT = Symbol('timed-out');
async function timeBoxed<T>(p: Promise<T>): Promise<T | typeof TIMED_OUT> {
  return Promise.race([
    p,
    new Promise<typeof TIMED_OUT>((resolve) => setTimeout(() => resolve(TIMED_OUT), NATIVE_CALL_TIMEOUT_MS)),
  ]);
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Initialize push notifications. Call this once on app startup.
 * Registers for push, listens for the device token, and handles
 * incoming notifications.
 */
export async function initPushNotifications(
  onNotification?: PushNotificationHandler,
): Promise<PushRegistrationResult> {
  if (_initialized) {
    return { success: true, token: _deviceToken ?? undefined };
  }

  const plugin = await getPlugin();
  if (!plugin) {
    return { success: false, error: 'Push notifications not available (not native platform)' };
  }

  if (onNotification) {
    _notificationHandler = onNotification;
  }

  try {
    // Check current permission status
    const permStatus: PermissionStatus | typeof TIMED_OUT = await timeBoxed(plugin.checkPermissions());
    if (permStatus === TIMED_OUT) {
      return { success: false, error: 'checkPermissions-timeout' };
    }

    if (permStatus.receive === 'prompt') {
      // No timeout here: this call legitimately waits on the user's answer
      // to the iOS/Android permission dialog.
      const result = await plugin.requestPermissions();
      if (result.receive !== 'granted') {
        return { success: false, error: 'Push notification permission denied' };
      }
    } else if (permStatus.receive !== 'granted') {
      return { success: false, error: `permission-${permStatus.receive}` };
    }

    // Attach listeners BEFORE register(). The 'registration' event can fire
    // synchronously during register() on iOS; if the listener isn't attached
    // first the token is lost and no device token is ever saved.
    plugin.addListener('registration', (token) => {
      console.log('[Push] Device token:', token.value);
      _deviceToken = token.value;
      // If a user is already signed in, persist immediately. Otherwise
      // saveDeviceToken() will persist once it's called with an identity.
      if (_pendingIdentity) {
        void persistDeviceToken(token.value, _pendingIdentity);
      }
    });

    // Listen for registration errors
    plugin.addListener('registrationError', (error) => {
      console.error('[Push] Registration error:', error);
    });

    // Listen for notifications received while app is in foreground
    plugin.addListener('pushNotificationReceived', (notification) => {
      console.log('[Push] Received:', notification);
      if (_notificationHandler) {
        _notificationHandler({
          title: notification.title ?? undefined,
          body: notification.body ?? undefined,
          data: notification.data ?? undefined,
        });
      }
    });

    // Listen for notification tap (opened from background/killed)
    plugin.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[Push] Action performed:', action);
      if (_notificationHandler && action.notification) {
        _notificationHandler({
          title: action.notification.title ?? undefined,
          body: action.notification.body ?? undefined,
          data: action.notification.data ?? undefined,
        });
      }
    });

    // Register with APNs / FCM now that all listeners are in place.
    const registered = await timeBoxed(plugin.register());
    if (registered === TIMED_OUT) {
      return { success: false, error: 'register-timeout' };
    }

    _initialized = true;
    return { success: true, token: _deviceToken ?? undefined };
  } catch (err) {
    console.error('[Push] Init error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Get the current device push token (APNs or FCM token).
 * Returns null if not registered or not native.
 */
export function getDeviceToken(): string | null {
  return _deviceToken;
}

/**
 * POST a device token to the server. Shared by the registration listener
 * (token arrives after login) and saveDeviceToken (login after token).
 */
async function persistDeviceToken(
  token: string,
  identity: { providerId: string; authUserId: string },
): Promise<boolean> {
  try {
    const platform = Capacitor.getPlatform(); // 'ios' or 'android'
    const res = await fetch('/api/push/register-device', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        providerId: identity.providerId,
        authUserId: identity.authUserId,
        token,
        platform,
      }),
    });
    return res.ok;
  } catch (err) {
    console.error('[Push] Failed to save device token:', err);
    return false;
  }
}

/**
 * Save the device token to Supabase for server-side push targeting.
 * Call this after successful login / session restore to associate the token
 * with a provider.
 *
 * The APNs/FCM token and this call race (the token registers asynchronously
 * at startup). We record the identity so the 'registration' listener can
 * persist the token the moment it arrives; if the token is already here we
 * persist immediately. Either order results in a saved, active token.
 */
export async function saveDeviceToken(
  providerId: string,
  authUserId: string,
): Promise<boolean> {
  _pendingIdentity = { providerId, authUserId };

  if (_deviceToken) {
    return persistDeviceToken(_deviceToken, _pendingIdentity);
  }

  // Token not back from APNs/FCM yet — the 'registration' listener will
  // persist it as soon as it arrives.
  return false;
}

/**
 * Set or update the notification handler.
 */
export function setNotificationHandler(handler: PushNotificationHandler): void {
  _notificationHandler = handler;
}

/**
 * Remove all delivered notifications from the notification center.
 */
export async function clearNotifications(): Promise<void> {
  const plugin = await getPlugin();
  if (!plugin) return;

  try {
    await plugin.removeAllDeliveredNotifications();
  } catch {
    // Ignore
  }
}
